import { Button, ErrorState, Spinner } from "@common/index";
import { ComponentColor } from "@common/types";
import { ROUTES } from "@routing/routes";
import { AddPaymentMethodPanel } from "../AddPaymentMethodPanel";
import { ASK_ADMINISTRATOR } from "../_helpers/askAdministrator";
import { useOrganizationRole } from "../_helpers/useOrganizationRole";

// What anyone but the Admin is told instead, by the Admin's action: the card is
// replaced on the membership tab, or the first one is added here.
const MEMBER_NEXT = {
    update: ASK_ADMINISTRATOR.update,
    addInline: ASK_ADMINISTRATOR.add,
};

export interface PaymentMethodActionForRoleProps {
    userId: string;
    /** Said to every reader, first. */
    lead: string;
    /** Said to the organization's Admin only, after the lead. */
    adminNext?: string;
    /** Said to every reader, last, once the role is known. */
    tail?: string;
    /**
     * What the Admin is offered: Update Payment Method, a link to the
     * membership tab (the default), or the first card's form inline.
     */
    adminAction?: "update" | "addInline";
}

// The body and the actions of a wall branch whose fix is a card (#368 PO
// ruling, 2026-11-03). Only the organization's Admin may save a card
// (CreatePaymentMethod requires the organization Admin role), so only an Admin
// is offered a card action; anyone else is told to ask their administrator.
// The role comes from organizations/me, read as PaymentReturned's AskForCard
// reads it: until the read answers only the lead is said, and a failed read
// says so and offers Retry rather than guessing a role. A user organizations/me
// lists in no organization, or who is not on its roles, is not an Admin.
export const PaymentMethodActionForRole = ({
    userId,
    lead,
    adminNext,
    tail,
    adminAction = "update",
}: PaymentMethodActionForRoleProps) => {
    const { readStatus, reload, organization, isAdmin } =
        useOrganizationRole(userId);

    const support = (
        <Button
            to={ROUTES.SUPPORT}
            color={ComponentColor.Primary}
            outline
            className="normal-case"
        >
            Contact Support
        </Button>
    );

    if (readStatus === "loading" && !organization) {
        return (
            <>
                <p className="text-base mt-6">{lead}</p>
                <div className="flex justify-center py-10">
                    <Spinner size="lg" />
                </div>
                <div className="mt-6">{support}</div>
            </>
        );
    }

    if (readStatus === "failed" && !organization) {
        return (
            <>
                <p className="text-base mt-6">{lead}</p>
                <ErrorState
                    message="Couldn't load your organization."
                    onRetry={() => void reload()}
                />
                <div className="mt-6">{support}</div>
            </>
        );
    }

    if (!organization || !isAdmin) {
        return (
            <>
                <p className="text-base mt-6">
                    {[lead, MEMBER_NEXT[adminAction], tail]
                        .filter(Boolean)
                        .join(" ")}
                </p>
                <div className="mt-8">{support}</div>
            </>
        );
    }

    const adminText = [lead, adminNext, tail].filter(Boolean).join(" ");

    if (adminAction === "addInline") {
        return (
            <>
                <p className="text-base mt-6">{adminText}</p>
                <div className="mt-8">
                    {/* Handed the id this component has just read, so the form
                        does not read organizations/me a second time. */}
                    <AddPaymentMethodPanel organizationId={organization.id} />
                </div>
                <div className="mt-6">{support}</div>
            </>
        );
    }

    return (
        <>
            <p className="text-base mt-6">{adminText}</p>
            <div className="mt-8 flex flex-wrap gap-4">
                <Button
                    to={ROUTES.ACCOUNT_MEMBERSHIP}
                    color={ComponentColor.Primary}
                    className="normal-case"
                >
                    Update Payment Method
                </Button>
                {support}
            </div>
        </>
    );
};
